import { useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'

import { usePrevious } from '../hooks'
import {
  addNotification as addNotificationAction,
  removeNotification as removeNotificationAction
} from '../notifications/notifications-actions'
import type { NotificationType } from '../notifications/types'
import { TdsModalError as ModalError } from '../shared/TdsModalError'
import type { GlobalState } from '../types'
import { clearError } from './upload-actions'
import {
  EXCEEDED_MAX,
  EXCEEDED_MAX_CUMULATIVE,
  EXCEEDED_MAX_QTY,
  MAX_QUANTITY,
  MAX_UPLOAD_SIZE
} from './upload-constants'
import { getErrors } from './upload-selectors'

type UploadNotificationsProps = {
  onCloseError(arg: string): void
  errors: Record<string, string>
  addNotification(arg: NotificationType): void
  removeNotification(name: string): void
}


const UploadNotificationsComponent = (props: UploadNotificationsProps) => {
  const { errors, addNotification, removeNotification, onCloseError } = props

  const prevProps = usePrevious(props)

  const handleCloseExceededMaxQty = () => onCloseError(EXCEEDED_MAX_QTY)
  const handleCloseExceededMax = () => onCloseError(EXCEEDED_MAX)

  const handleErrorNotification = useCallback(() => {
    removeNotification('maxCumulative')
    if (errors[EXCEEDED_MAX_CUMULATIVE]) {
      addNotification({
        name: 'maxCumulative',
        body: 'The file(s) you are trying to upload is over the maximum upload size of 30 MB by '
          + `<strong>${errors[EXCEEDED_MAX_CUMULATIVE]} MB</strong>.`,
        severity: 'error',
        title: 'FILE SIZE LIMIT EXCEEDED'
      })
    }
  }, [removeNotification, addNotification, errors])

  // updated from componentDidMount
  useEffect(() => {
    handleErrorNotification()
  }, [handleErrorNotification])

  // updated from componentDidUpdate
  useEffect(() => {
    if (prevProps) {
      handleErrorNotification()
    }
  }, [handleErrorNotification, prevProps])

  const componentWillUnmount = useCallback(() => {
    removeNotification('maxCumulative')
  }, [removeNotification])

  useEffect(() => componentWillUnmount, [componentWillUnmount])

  return (
    <>
      {errors[EXCEEDED_MAX] !== undefined &&
        <ModalError handleClose={handleCloseExceededMax} title='Error' >
          The file(s) you are trying to upload {errors[EXCEEDED_MAX]} is over the maximum upload
          size of {MAX_UPLOAD_SIZE / 1000 / 1000} MB.
        </ModalError>
      }
      {errors[EXCEEDED_MAX_QTY] !== undefined &&
        <ModalError handleClose={handleCloseExceededMaxQty} title='Error' >
          The file(s) you are trying to add is over the maximum quantity limit of {MAX_QUANTITY}.
        </ModalError>
      }
    </>
  )
}

const mapStateToProps = (state: GlobalState) => ({
  errors: getErrors(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onCloseError: (name: string) => dispatch(clearError(name)),
  removeNotification: (name: string) => dispatch(removeNotificationAction(name)),
  addNotification: (notification: NotificationType) => dispatch(addNotificationAction(notification))
})

const UploadNotifications =
  connect(mapStateToProps, mapDispatchToProps)(UploadNotificationsComponent)

export {
  UploadNotifications,
  UploadNotificationsComponent // for testing without HOC
}
